import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTeacherAuth }from '../auth/TeacherAuthContext';
import axios from 'axios';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Paper, 
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import Icon from '@mui/icons-material/Person';
import AOS from 'aos';
import 'aos/dist/aos.css';

const TeacherLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useTeacherAuth();
  const [email, setEmail] = useState('');
  const [tscNumber, setTscNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const from = location.state?.from?.pathname || "/teacher";

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !tscNumber) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        'https://kidsm.vercel.app/api/schools/teacher-login',
        { email, tscNumber }
      );

      if (response.status === 200) {
        const { teacherId, teacherName, gender, schoolName, schoolId } = response.data;
        login({
          teacherId,
          teacherName,
          gender,
          schoolName,
          schoolId
        });
        navigate(from, { replace: true });
      }
    } catch (error) {
      if (error.response) {
        setError(error.response.data.message || 'Login failed. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const welcomeText = "Welcome Back, Teacher!";
  const subtitleText = "Empowering Educators With Digital Tools For Better Learning Outcomes";

  return (
    <Container 
      maxWidth="sm" 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e1e5eb 100%)',
        p: 2
      }}
    >
      {/* Animated Welcome Section */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
        data-aos="fade-down"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            backgroundColor: '#6200ee',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
            boxShadow: '0 4px 20px rgba(98, 0, 238, 0.3)'
          }}
        >
          <Icon 
            sx={{ 
              fontSize: 48, 
              color: 'white',
              animation: 'pulse 2s infinite'
            }} 
          />
        </motion.div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{
              fontWeight: 'bold',
              mb: 3,
              background: 'linear-gradient(to right, #6200ee, #03dac6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {welcomeText}
          </Typography>
        </motion.div>
      </Box>

      {/* Login Form */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
          data-aos="zoom-in"
        >
          {error && (
            <motion.div
              initial={{ x: -10 }}
              animate={{ x: 0 }}
              transition={{ type: 'spring', stiffness: 500 }}
            >
              <Typography 
                color="error" 
                align="center" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500
                }}
              >
                {error}
              </Typography>
            </motion.div>
          )}

          <Box 
            component="form" 
            onSubmit={handleLogin} 
            sx={{ mt: 2 }}
          >
            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#6200ee',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6200ee',
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="TSC Number"
              variant="outlined"
              margin="normal"
              value={tscNumber}
              onChange={(e) => setTscNumber(e.target.value)}
              disabled={isLoading}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#6200ee',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6200ee',
                  },
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              sx={{ 
                mt: 1, 
                mb: 2, 
                height: '48px',
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(98, 0, 238, 0.2)',
                '&:hover': {
                  boxShadow: '0 6px 8px rgba(98, 0, 238, 0.3)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Login'
              )}
            </Button>
          </Box>
        </Paper>
      </motion.div>

      {/* Footer */}
      <Box 
        sx={{ 
          mt: 4,
          textAlign: 'center'
        }}
      >
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          Powered by Fusion-Xe
        </Typography>
      </Box>
    </Container>
  );
};

export default TeacherLogin;